import logger from '@/lib/logger';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Lightning, Barbell, Target, PersonSimpleRun, Flame, Brain, ArrowRight, ArrowLeft, Check } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS = [
  { id: 'beginner', label: 'BEGINNER', desc: 'New to working out or returning after a break' },
  { id: 'intermediate', label: 'INTERMEDIATE', desc: 'Regular workouts for 3-6 months' },
  { id: 'advanced', label: 'ADVANCED', desc: 'Consistent training for 6+ months' },
];

const GOALS = [
  { id: 'fat_loss', label: 'Fat Loss', icon: Flame },
  { id: 'muscle_tone', label: 'Muscle Tone', icon: Barbell },
  { id: 'flexibility', label: 'Flexibility', icon: PersonSimpleRun },
  { id: 'stress_relief', label: 'Stress Relief', icon: Brain },
  { id: 'endurance', label: 'Endurance', icon: Target },
];

const BODY_PARTS = [
  { id: 'full_body', label: 'Full Body' },
  { id: 'abs', label: 'Abs & Core' },
  { id: 'arms', label: 'Arms' },
  { id: 'chest', label: 'Chest' },
  { id: 'legs', label: 'Legs' },
  { id: 'back', label: 'Back' },
  { id: 'shoulders', label: 'Shoulders' },
];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [level, setLevel] = useState('beginner');
  const [goals, setGoals] = useState([]);
  const [bodyFocus, setBodyFocus] = useState([]);
  const [diet, setDiet] = useState('balanced');
  const [loading, setLoading] = useState(false);
  const { updateUser } = useAuth();
  const navigate = useNavigate();

  const toggleItem = (arr, setArr, id) => {
    setArr(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const { data } = await axios.put(`${API}/user/onboarding`, {
        fitness_level: level,
        goals: goals.length ? goals : ['fat_loss'],
        body_focus: bodyFocus.length ? bodyFocus : ['full_body'],
        dietary_preferences: diet,
      });
      updateUser(data);
      navigate('/dashboard');
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    // Step 0: Fitness Level
    <div key="level" className="space-y-4" data-testid="onboarding-level">
      <h2 className="text-4xl font-black tracking-tighter">YOUR FITNESS LEVEL</h2>
      <p className="text-[#A1A1AA]">We'll customize your experience based on where you are now.</p>
      <div className="space-y-3 mt-6">
        {LEVELS.map(l => (
          <button
            key={l.id}
            data-testid={`level-${l.id}`}
            onClick={() => setLevel(l.id)}
            className={`w-full text-left p-5 tactical-card transition-all duration-200 ${level === l.id ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'hover:border-white/20'}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-lg">{l.label}</div>
                <div className="text-sm text-[#A1A1AA]">{l.desc}</div>
              </div>
              {level === l.id && <Check weight="bold" className="w-6 h-6 text-[#FF3B30]" />}
            </div>
          </button>
        ))}
      </div>
    </div>,
    // Step 1: Goals
    <div key="goals" className="space-y-4" data-testid="onboarding-goals">
      <h2 className="text-4xl font-black tracking-tighter">YOUR GOALS</h2>
      <p className="text-[#A1A1AA]">Select one or more goals. We'll build your plan around these.</p>
      <div className="grid grid-cols-2 gap-3 mt-6">
        {GOALS.map(g => (
          <button
            key={g.id}
            data-testid={`goal-${g.id}`}
            onClick={() => toggleItem(goals, setGoals, g.id)}
            className={`p-5 tactical-card text-left transition-all duration-200 ${goals.includes(g.id) ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'hover:border-white/20'}`}
          >
            <g.icon weight="bold" className={`w-8 h-8 mb-2 ${goals.includes(g.id) ? 'text-[#FF3B30]' : 'text-[#71717A]'}`} />
            <div className="font-bold">{g.label}</div>
          </button>
        ))}
      </div>
    </div>,
    // Step 2: Body Focus
    <div key="body" className="space-y-4" data-testid="onboarding-body-focus">
      <h2 className="text-4xl font-black tracking-tighter">FOCUS AREAS</h2>
      <p className="text-[#A1A1AA]">Which areas do you want to target most?</p>
      <div className="grid grid-cols-2 gap-3 mt-6">
        {BODY_PARTS.map(b => (
          <button
            key={b.id}
            data-testid={`body-${b.id}`}
            onClick={() => toggleItem(bodyFocus, setBodyFocus, b.id)}
            className={`p-4 tactical-card text-center font-bold transition-all duration-200 ${bodyFocus.includes(b.id) ? 'border-[#FF3B30] bg-[#FF3B30]/5 text-[#FF3B30]' : 'hover:border-white/20 text-[#A1A1AA]'}`}
          >
            {b.label}
          </button>
        ))}
      </div>
    </div>,
    // Step 3: Diet
    <div key="diet" className="space-y-4" data-testid="onboarding-diet">
      <h2 className="text-4xl font-black tracking-tighter">NUTRITION STYLE</h2>
      <p className="text-[#A1A1AA]">This helps us personalize your meal recommendations.</p>
      <div className="space-y-3 mt-6">
        {['balanced', 'vegetarian', 'vegan', 'keto', 'high_protein'].map(d => (
          <button
            key={d}
            data-testid={`diet-${d}`}
            onClick={() => setDiet(d)}
            className={`w-full text-left p-4 tactical-card transition-all duration-200 ${diet === d ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'hover:border-white/20'}`}
          >
            <div className="flex items-center justify-between">
              <span className="font-bold uppercase">{d.replace('_', ' ')}</span>
              {diet === d && <Check weight="bold" className="w-5 h-5 text-[#FF3B30]" />}
            </div>
          </button>
        ))}
      </div>
    </div>,
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6" data-testid="onboarding-page">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center gap-2 mb-8">
          <Lightning weight="fill" className="text-[#FF3B30] w-7 h-7" />
          <span className="font-['Barlow_Condensed'] text-xl font-black uppercase tracking-tight">GRIT.FIT</span>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {[0, 1, 2, 3].map(idx => (
            <div key={`step-${idx}`} className={`h-1 flex-1 transition-all duration-300 ${idx <= step ? 'bg-[#FF3B30]' : 'bg-white/10'}`} />
          ))}
        </div>

        <div className="label-tactical text-[#FF3B30] mb-2">Step {step + 1} of 4</div>

        {/* Content */}
        {steps[step]}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="btn-secondary px-6 py-3 flex items-center gap-2" data-testid="onboarding-back">
              <ArrowLeft weight="bold" className="w-4 h-4" /> Back
            </button>
          ) : <div />}
          {step < 3 ? (
            <button onClick={() => setStep(step + 1)} className="btn-primary px-6 py-3 flex items-center gap-2" data-testid="onboarding-next">
              Next <ArrowRight weight="bold" className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleComplete} disabled={loading} className="btn-primary px-8 py-3 flex items-center gap-2" data-testid="onboarding-complete">
              {loading ? 'Saving...' : 'Start Training'} <ArrowRight weight="bold" className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
